import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { scannerContract } from '@/modules/Contract/modules/Process/services'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'

import { renderFileItem } from '@/utils/function'
import { FileMimeType } from 'vnetwork-ui/dist/components/DragDropFileInput'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface ScannerContractModalProps extends ModalProps {
    contractID: number
}

const ScannerContractModal = ({
    contractID,
    onClose,
    open,
}: ScannerContractModalProps) => {
    const { uploadAsync, isUploading } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_CUSTOMER,
    })
    const { t } = useTranslation()

    const { refetch } = useQueryClientCallback({
        pattern: 'contracts',
        mode: 'contains',
    })

    const formControl = useForm<{
        attachments: File[]
        contract: { replace_old_attachment: boolean }
    }>({
        defaultValues: {
            attachments: [],
            contract: {
                replace_old_attachment: true,
            },
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: scannerContract,
        },
        {
            successMessage: t('scanner-contract-success'),
            reactQueryKeys: QUERY_KEYS.CONTRACTS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const handleCreate = async (values: {
        attachments: File[]
        contract: { replace_old_attachment: boolean }
    }) => {
        let attachments: Attachment[] = []
        if (values?.attachments) {
            const attachmentUploadeds = await uploadAsync(
                values?.attachments.map((attachment) => attachment as File)
            )

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
        }

        mutateAsync({
            contractID: contractID,
            attachments: attachments,
            contract: {
                replace_old_attachment:
                    values?.contract?.replace_old_attachment,
            },
        })
    }
    return (
        <Dialog
            title={t('contracts.customer.scanner')}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending || isUploading,
                    },
                    {
                        title: t('contracts.customer.scanner.submit'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending || isUploading,
                        disabled: isPending || isUploading,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'contract.replace_old_attachment',
                            type: 'checkbox',
                            label: t(
                                'contracts.customer.scanner.replace_old_attachment'
                            ),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            renderFileItem: renderFileItem,
                            label: t('attachments'),
                            multiple: true,
                            accept: [
                                'application/pdf',
                            ] as unknown as FileMimeType[],
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default ScannerContractModal
